import type { FallbackRender } from "@sentry/react";

/**
 * Minimal fallback UI rendered by Sentry.ErrorBoundary when a component
 * subtree crashes. Keeps the user in a recoverable state — shows the
 * error summary, a reset button (remounts the tree), and a reload
 * button as last resort.
 */
export const SentryFallback: FallbackRender = ({ error, resetError }) => {
  const message = error instanceof Error ? error.message : String(error);
  return (
    <div className="flex h-screen flex-col items-center justify-center gap-4 bg-cream-50 p-8 text-coffee-900 dark:bg-coffee-950 dark:text-cream-100">
      <div className="max-w-md text-center">
        <h1 className="font-serif-display mb-2 text-display-md font-semibold">Something went wrong</h1>
        <p className="mb-4 text-body text-coffee-600 dark:text-cream-400">
          Lexiun hit an unexpected error. The team has been notified.
        </p>
        <pre className="mb-6 max-h-40 overflow-auto rounded-lg bg-cream-100 p-3 text-left text-xs text-coffee-700 dark:bg-coffee-900 dark:text-cream-300">
          {message}
        </pre>
        <div className="flex justify-center gap-3">
          <button
            type="button"
            onClick={resetError}
            className="rounded-lg bg-coffee-900 px-4 py-2 text-body-sm font-semibold text-white transition-colors hover:bg-coffee-800 dark:bg-cream-100 dark:text-coffee-900 dark:hover:bg-cream-200"
          >
            Try again
          </button>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="rounded-lg border border-cream-300 px-4 py-2 text-body-sm font-medium text-coffee-700 transition-colors hover:bg-cream-100/60 dark:border-coffee-700 dark:text-cream-200 dark:hover:bg-coffee-800/60"
          >
            Reload page
          </button>
        </div>
      </div>
    </div>
  );
};
